/** The endpoint helper is the API's (shared/endpoint.ts), so admin contracts mount on the API unchanged. */
export * from "./shared/endpoint.ts";

import type { z } from "zod";
import { endpoint, type Endpoint } from "./shared/endpoint.ts";
import type { Capability } from "./permissions.ts";

/** Capability + level an admin endpoint requires (403 `forbidden` otherwise). The API mounts the same guard. */
export type Permission = readonly [Capability, "read" | "write"];

export type AdminEndpoint<P extends z.ZodType = z.ZodType, Q extends z.ZodType = z.ZodType, B extends z.ZodType = z.ZodType, R extends z.ZodType = z.ZodType> = Endpoint<
  P,
  Q,
  B,
  R
> & { permission: Permission };

/**
 * `endpoint()` + the permission it needs. Every `/admin/*` data endpoint uses this (auth endpoints use `endpoint`).
 *   adminEndpoint({ id: "admin.finance.refunds", method: "GET", path: "/admin/finance/refunds", response: …, permission: ["finance", "read"] })
 */
export function adminEndpoint<P extends z.ZodType = z.ZodNever, Q extends z.ZodType = z.ZodNever, B extends z.ZodType = z.ZodNever, R extends z.ZodType = z.ZodType>(
  def: AdminEndpointDef<P, Q, B, R>,
  // NoInfer: inside `{ … } satisfies EndpointGroup` the contextual return type would otherwise widen P / Q / B to ZodType.
): AdminEndpoint<NoInfer<P>, NoInfer<Q>, NoInfer<B>, NoInfer<R>> {
  const { permission, ...rest } = def;
  return { ...endpoint<P, Q, B, R>(rest), permission };
}

interface AdminEndpointDef<P extends z.ZodType, Q extends z.ZodType, B extends z.ZodType, R extends z.ZodType> extends Endpoint<P, Q, B, R> {
  permission: Permission;
}

export function permissionOf(ep: Endpoint): Permission | undefined {
  return (ep as Partial<AdminEndpoint>).permission;
}
